import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, Upload, Plus, X, Home as HomeIcon } from "lucide-react";
import { API } from "@/lib/auth";

/** Edits the singleton site_content doc (hero + FAQs). */
export default function HomepageEditor() {
  const [content, setContent] = useState(null);
  const [heroDraft, setHeroDraft] = useState(null);
  const [faqDraft, setFaqDraft] = useState(null);
  const [savingHero, setSavingHero] = useState(false);
  const [savingFaqs, setSavingFaqs] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/site/content`);
      setContent(r.data);
      setHeroDraft(r.data?.hero || {});
      setFaqDraft(Array.isArray(r.data?.faqs) ? r.data.faqs : []);
    } catch (e) {
      toast.error("Could not load homepage content");
    }
  };
  useEffect(() => { load(); }, []);

  const heroDirty = JSON.stringify(heroDraft) !== JSON.stringify(content?.hero || {});
  const faqDirty = JSON.stringify(faqDraft) !== JSON.stringify(content?.faqs || []);

  const saveHero = async () => {
    setSavingHero(true);
    try {
      await axios.patch(`${API}/admin/site/content/hero`, { data: heroDraft });
      toast.success("Hero saved — refresh the homepage to see it live.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSavingHero(false); }
  };

  const saveFaqs = async () => {
    setSavingFaqs(true);
    try {
      await axios.patch(`${API}/admin/site/content/faqs`, { data: { items: faqDraft } });
      toast.success("FAQs saved");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSavingFaqs(false); }
  };

  const uploadHeroImage = async (files) => {
    const file = files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await axios.post(`${API}/admin/site/hero/image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setHeroDraft((h) => ({ ...h, image: r.data.image }));
      toast.success("Hero image uploaded — remember to save.");
      // Reflect in `content` so heroDirty stays accurate for the newly saved image
      setContent((c) => ({ ...c, hero: { ...c.hero, image: r.data.image } }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  if (!heroDraft || faqDraft === null) {
    return <div className="p-10 text-center text-[hsl(var(--onyx)/0.5)]">Loading homepage content…</div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-homepage-panel">
      {/* ————— HERO ————— */}
      <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="overline flex items-center gap-2"><HomeIcon size={14} /> Hero section</div>
            <p className="text-xs text-[hsl(var(--onyx)/0.6)] mt-1">Top of the homepage: 3-line headline, subhead, image, review quote.</p>
          </div>
          <button onClick={saveHero} disabled={!heroDirty || savingHero} data-testid="hero-save" className="btn-pill btn-primary disabled:opacity-50">
            <Save size={14} /> {savingHero ? "Saving…" : heroDirty ? "Save hero" : "Saved"}
          </button>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
          <div className="space-y-3">
            <Text label="Eyebrow label (badge)" value={heroDraft.eyebrow || ""} onChange={(v) => setHeroDraft({ ...heroDraft, eyebrow: v })} testid="hero-eyebrow" />
            <div className="grid gap-3 sm:grid-cols-3">
              <Text label="Line 1" value={heroDraft.title_line_1 || ""} onChange={(v) => setHeroDraft({ ...heroDraft, title_line_1: v })} testid="hero-line-1" />
              <Text label="Line 2 (italic)" value={heroDraft.title_line_2 || ""} onChange={(v) => setHeroDraft({ ...heroDraft, title_line_2: v })} testid="hero-line-2" />
              <Text label="Line 3 (gold)" value={heroDraft.title_line_3 || ""} onChange={(v) => setHeroDraft({ ...heroDraft, title_line_3: v })} testid="hero-line-3" />
            </div>
            <TextArea label="Subhead" rows={3} value={heroDraft.subhead || ""} onChange={(v) => setHeroDraft({ ...heroDraft, subhead: v })} testid="hero-subhead" />
            <div className="grid gap-3 sm:grid-cols-2">
              <Text label="Stat: Years label" value={heroDraft.stat_years || ""} onChange={(v) => setHeroDraft({ ...heroDraft, stat_years: v })} testid="hero-stat-years" />
              <Text label="Stat: Reviews score" value={heroDraft.stat_reviews || ""} onChange={(v) => setHeroDraft({ ...heroDraft, stat_reviews: v })} testid="hero-stat-reviews" />
            </div>
            <TextArea label="Review card quote" rows={2} value={heroDraft.review_quote || ""} onChange={(v) => setHeroDraft({ ...heroDraft, review_quote: v })} testid="hero-review-quote" />
            <Text label="Review card author" value={heroDraft.review_author || ""} onChange={(v) => setHeroDraft({ ...heroDraft, review_author: v })} testid="hero-review-author" />
          </div>

          <div className="space-y-3">
            <div className="aspect-[4/5] rounded-2xl overflow-hidden bg-blush/40 border border-[hsl(var(--onyx)/0.08)]">
              {heroDraft.image ? (
                <img src={heroDraft.image} alt="Hero preview" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-xs text-[hsl(var(--onyx)/0.45)]">No image</div>
              )}
            </div>
            <label htmlFor="hero-image-file" data-testid="hero-image-uploader"
              className={`block w-full text-center py-4 rounded-2xl border-2 border-dashed cursor-pointer transition-colors ${uploading ? "border-[hsl(var(--onyx)/0.1)] text-[hsl(var(--onyx)/0.35)]" : "border-[hsl(var(--onyx)/0.2)] text-[hsl(var(--onyx)/0.7)] hover:border-[hsl(var(--onyx))] hover:text-[hsl(var(--onyx))]"}`}>
              {uploading ? "Uploading…" : <><Upload size={16} className="inline mr-2" /> Upload hero image</>}
              <div className="text-[11px] mt-1 text-[hsl(var(--onyx)/0.5)]">Portrait 4:5 recommended</div>
            </label>
            <input id="hero-image-file" type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" disabled={uploading}
              onChange={(e) => { uploadHeroImage(e.target.files); e.target.value = ""; }} className="hidden" />
            <Text label="Image URL (or use uploader above)" value={heroDraft.image || ""} onChange={(v) => setHeroDraft({ ...heroDraft, image: v })} testid="hero-image-url" />
          </div>
        </div>
      </div>

      {/* ————— FAQs ————— */}
      <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6 space-y-4" data-testid="homepage-faqs">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="overline">Frequently asked</div>
            <p className="text-xs text-[hsl(var(--onyx)/0.6)] mt-1">Homepage FAQ accordion. Add, edit, reorder or remove — save when you're done.</p>
          </div>
          <button onClick={saveFaqs} disabled={!faqDirty || savingFaqs} data-testid="faqs-save" className="btn-pill btn-primary disabled:opacity-50">
            <Save size={14} /> {savingFaqs ? "Saving…" : faqDirty ? "Save FAQs" : "Saved"}
          </button>
        </div>

        <div className="space-y-3">
          {faqDraft.map((faq, i) => (
            <div key={i} className="rounded-2xl border border-[hsl(var(--onyx)/0.08)] p-4 space-y-2" data-testid={`faq-edit-${i}`}>
              <div className="flex items-start gap-2">
                <div className="flex-1 space-y-2">
                  <Text label="Question" value={faq.q || ""} onChange={(v) => {
                    const next = [...faqDraft]; next[i] = { ...next[i], q: v }; setFaqDraft(next);
                  }} testid={`faq-q-${i}`} />
                  <TextArea label="Answer" rows={3} value={faq.a || ""} onChange={(v) => {
                    const next = [...faqDraft]; next[i] = { ...next[i], a: v }; setFaqDraft(next);
                  }} testid={`faq-a-${i}`} />
                </div>
                <div className="flex flex-col gap-1 pt-6">
                  <button onClick={() => {
                    if (i === 0) return;
                    const next = [...faqDraft];
                    [next[i - 1], next[i]] = [next[i], next[i - 1]];
                    setFaqDraft(next);
                  }} disabled={i === 0} type="button" className="text-xs px-2 py-1 rounded-lg border border-[hsl(var(--onyx)/0.15)] disabled:opacity-30">↑</button>
                  <button onClick={() => {
                    if (i === faqDraft.length - 1) return;
                    const next = [...faqDraft];
                    [next[i + 1], next[i]] = [next[i], next[i + 1]];
                    setFaqDraft(next);
                  }} disabled={i === faqDraft.length - 1} type="button" className="text-xs px-2 py-1 rounded-lg border border-[hsl(var(--onyx)/0.15)] disabled:opacity-30">↓</button>
                  <button onClick={() => setFaqDraft(faqDraft.filter((_, j) => j !== i))}
                    type="button" data-testid={`faq-remove-${i}`} className="text-xs px-2 py-1 rounded-lg border border-[hsl(var(--onyx)/0.15)] text-[hsl(var(--burgundy))]">
                    <X size={12} />
                  </button>
                </div>
              </div>
            </div>
          ))}
          {faqDraft.length === 0 && (
            <div className="text-xs text-[hsl(var(--onyx)/0.5)] italic px-2">No FAQs yet — add one below.</div>
          )}
          <button
            type="button"
            onClick={() => setFaqDraft([...faqDraft, { q: "", a: "" }])}
            data-testid="faq-add"
            className="btn-pill btn-outline !text-xs"
          >
            <Plus size={12} /> Add FAQ
          </button>
        </div>
      </div>
    </div>
  );
}

function Text({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">{label}</label>
      <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
        className="w-full h-11 mt-1 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm outline-none focus:border-[hsl(var(--onyx))]" />
    </div>
  );
}
function TextArea({ label, value, onChange, rows = 3, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">{label}</label>
      <textarea value={value ?? ""} rows={rows} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full mt-1 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 py-2 text-sm outline-none focus:border-[hsl(var(--onyx))] resize-y" />
    </div>
  );
}
